import { arrayMove } from '@dnd-kit/sortable';

import { formatEUR } from './money';
import { ALLOCATION_TYPES } from './constants';

const SAVINGS_GROUP_KEYWORDS = [
  'poupança',
  'poupanca',
  'investimento',
  'investir',
  'savings',
];

const WANTS_GROUP_KEYWORDS = ['desej', 'estilo', 'want', 'prazer'];

/**
 * @typedef {'essential' | 'lifestyle' | 'personal_investment'} CategoryAllocation
 * @typedef {'orphan' | CategoryAllocation} CategoryBucketId
 */

/** @type {CategoryBucketId[]} */
export const CATEGORY_BUCKET_ORDER = [
  'orphan',
  'essential',
  'lifestyle',
  'personal_investment',
];

/** Allocation buckets only (no orphan strip). */
export const ALLOCATION_BUCKET_IDS = [
  'essential',
  'lifestyle',
  'personal_investment',
];

/**
 * @param {object|null|undefined} category
 * @returns {CategoryAllocation}
 */
export function resolveCategoryAllocation(category) {
  if (category?.budget_allocation) {
    return category.budget_allocation;
  }
  const groupName = (category?.group_name || '').toLowerCase();
  if (SAVINGS_GROUP_KEYWORDS.some((kw) => groupName.includes(kw))) {
    return 'personal_investment';
  }
  return category?.is_essential === false ? 'lifestyle' : 'essential';
}

/**
 * @param {object[]} categories
 * @param {object[]} [orphans]
 * @returns {Record<CategoryBucketId, object[]>}
 */
export function groupCategoriesIntoBuckets(categories = [], orphans = []) {
  const orphanIds = new Set((orphans || []).map((o) => o.id));
  /** @type {Record<CategoryBucketId, object[]>} */
  const buckets = {
    orphan: [],
    essential: [],
    lifestyle: [],
    personal_investment: [],
  };
  for (const category of categories) {
    if (orphanIds.has(category.id)) {
      buckets.orphan.push(category);
      continue;
    }
    const allocation = resolveCategoryAllocation(category);
    buckets[allocation].push(category);
  }
  return buckets;
}

/**
 * Flat id order: orphan strip then the three method buckets.
 * @param {Record<CategoryBucketId, object[]>} buckets
 * @returns {string[]}
 */
export function flattenBucketOrder(buckets) {
  return CATEGORY_BUCKET_ORDER.flatMap((id) =>
    (buckets[id] || []).map((c) => (typeof c === 'string' ? c : c.id)),
  );
}

/** @param {Record<CategoryBucketId, string[]>} items */
export function flattenBucketIdLists(items) {
  return CATEGORY_BUCKET_ORDER.flatMap((id) => items[id] || []);
}

/**
 * Plan a drag move across / within buckets (pure — for DnD + tests).
 * @param {Record<CategoryBucketId, string[]>} items id lists per bucket
 * @param {{ activeId: string, overId: string|null, overBucketId?: CategoryBucketId|null }} move
 * @returns {{
 *   items: Record<CategoryBucketId, string[]>,
 *   fromBucket: CategoryBucketId|null,
 *   toBucket: CategoryBucketId|null,
 *   allocationChanged: boolean,
 *   orderedIds: string[],
 * } | null}
 */
export function planBucketMove(items, { activeId, overId, overBucketId = null }) {
  if (!activeId || (!overId && !overBucketId)) return null;

  const findBucket = (id) => {
    if (id && items[id]) return /** @type {CategoryBucketId} */ (id);
    return (
      CATEGORY_BUCKET_ORDER.find((key) => (items[key] || []).includes(id)) || null
    );
  };

  const fromBucket = findBucket(activeId);
  if (!fromBucket) return null;

  let toBucket =
    (overBucketId && items[overBucketId] ? overBucketId : null) ||
    findBucket(overId);
  if (!toBucket) return null;

  const next = {
    orphan: [...(items.orphan || [])],
    essential: [...(items.essential || [])],
    lifestyle: [...(items.lifestyle || [])],
    personal_investment: [...(items.personal_investment || [])],
  };

  if (fromBucket === toBucket) {
    const list = next[fromBucket];
    const oldIndex = list.indexOf(activeId);
    let newIndex = list.indexOf(overId);
    if (overId === toBucket || newIndex < 0) newIndex = list.length - 1;
    if (oldIndex < 0 || oldIndex === newIndex) return null;
    next[fromBucket] = arrayMove(list, oldIndex, newIndex);
    return {
      items: next,
      fromBucket,
      toBucket,
      allocationChanged: false,
      orderedIds: flattenBucketIdLists(next),
    };
  }

  // Dropping onto the orphan strip does not clear allocation — only classify out of it.
  if (toBucket === 'orphan') return null;

  const fromList = next[fromBucket].filter((id) => id !== activeId);
  const toList = [...next[toBucket]];
  let insertIndex = toList.indexOf(overId);
  if (overId === toBucket || insertIndex < 0) insertIndex = toList.length;
  toList.splice(insertIndex, 0, activeId);
  next[fromBucket] = fromList;
  next[toBucket] = toList;

  return {
    items: next,
    fromBucket,
    toBucket,
    allocationChanged: fromBucket !== toBucket,
    orderedIds: flattenBucketIdLists(next),
  };
}

/**
 * @param {object|null|undefined} category
 * @param {object} pt
 * @returns {string}
 */
export function formatCategoryLimitLabel(category, pt) {
  const raw = category?.default_monthly_budget;
  if (raw === null || raw === undefined || raw === '') {
    return pt.categories.buckets.noLimit;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return pt.categories.buckets.noLimit;
  return formatEUR(n);
}

/** @param {CategoryAllocation} allocation */
export function allocationBadgeClasses(allocation) {
  if (allocation === ALLOCATION_TYPES.PERSONAL_INVESTMENT) {
    return 'bg-sky-100 text-sky-900';
  }
  if (allocation === ALLOCATION_TYPES.LIFESTYLE) {
    return 'bg-amber-100 text-amber-900';
  }
  return 'bg-sage-100 text-sage-800';
}

/** Rótulo de classificação (lista, gráficos) — distinto do texto do formulário. */
export function allocationBadgeLabel(allocation, pt) {
  const labels = pt.categories.classification;
  if (allocation === ALLOCATION_TYPES.PERSONAL_INVESTMENT) {
    return labels.investment;
  }
  if (allocation === ALLOCATION_TYPES.LIFESTYLE) {
    return labels.nonEssential;
  }
  return labels.essential;
}

/**
 * Payload para criar/editar categoria na API.
 * @param {CategoryAllocation} allocation
 */
export function payloadFromCategoryAllocation(allocation) {
  return {
    budget_allocation: allocation,
    is_essential: allocation === ALLOCATION_TYPES.ESSENTIAL,
  };
}

/**
 * Classificação por defeito ao criar categoria a partir de um grupo do orçamento.
 * @param {object|null|undefined} group
 * @returns {CategoryAllocation}
 */
export function allocationForBudgetGroup(group) {
  if (!group) return 'essential';
  if (group.is_pay_yourself_first) return 'personal_investment';
  const name = (group.name || '').toLowerCase();
  if (SAVINGS_GROUP_KEYWORDS.some((kw) => name.includes(kw))) {
    return 'personal_investment';
  }
  if (WANTS_GROUP_KEYWORDS.some((kw) => name.includes(kw))) {
    return 'lifestyle';
  }
  return 'essential';
}
